import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, authFetch } from "../App";
import {
  Briefcase,
  ChevronRight,
  Clock,
  FolderOpen,
  User,
} from "lucide-react";
import ImpersonationBanner from "../components/ImpersonationBanner";
import WorkspaceMobile from "./WorkspaceMobile";

const API = "/api";

// Desktop: Simple project list for non-admin users
function DesktopWorkspace({ user, navigate }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await authFetch(`${API}/projects`);
        if (res.ok) {
          const data = await res.json();
          setProjects(data.filter((p) => p.status !== "abgeschlossen"));
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <ImpersonationBanner />
      <div className="max-w-4xl mx-auto py-8 px-4">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gray-900 flex items-center justify-center">
              <Briefcase className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Mein Arbeitsbereich</h1>
          </div>
          <p className="text-gray-500">Übersicht deiner zugewiesenen Projekte</p>
        </div>

        <div className="bg-gray-100 border border-gray-200 rounded-lg p-4 mb-6 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-900 flex items-center justify-center text-white font-semibold">
            {user?.name?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <div>
            <p className="font-medium text-gray-900">{user?.name}</p>
            <p className="text-sm text-gray-600">{user?.email}</p>
          </div>
        </div>

        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-500" />
            Meine Projekte
          </h2>
          <span className="text-sm text-gray-500">
            {projects.length} {projects.length === 1 ? "Projekt" : "Projekte"}
          </span>
        </div>

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gray-200" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
                    <div className="h-4 bg-gray-100 rounded w-1/4" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : projects.length > 0 ? (
          <div className="space-y-3">
            {projects.map((project) => (
              <div
                key={project.id}
                onClick={() => navigate(`/app/workspace/${project.id}`)}
                className="bg-white border border-gray-200 rounded-xl p-5 cursor-pointer transition-all hover:shadow-md hover:border-gray-400 group"
                data-testid={`workspace-project-${project.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-12 h-12 rounded-lg bg-gray-900 flex items-center justify-center flex-shrink-0">
                      <Briefcase className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{project.name}</h3>
                      {project.address && (
                        <p className="text-sm text-gray-500 truncate mt-0.5">
                          {typeof project.address === 'object'
                            ? [project.address.street, project.address.zip, project.address.city].filter(Boolean).join(', ')
                            : project.address}
                        </p>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <FolderOpen className="w-16 h-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Keine Projekte zugeordnet</h3>
            <p className="text-gray-500 text-center max-w-md">
              Dir wurden noch keine Projekte zugewiesen.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Main Component
export default function WorkspaceDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const isAdminOrManager = user?.role === "admin" || user?.role === "projektleiter";

  if (isAdminOrManager) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <User className="w-8 h-8 text-gray-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Admin-Bereich</h3>
        <p className="text-gray-500 text-center max-w-md mb-6">
          Als {user?.role === "admin" ? "Administrator" : "Projektleiter"} hast du Zugriff auf den vollständigen Admin-Bereich.
        </p>
        <button
          onClick={() => navigate("/app")}
          className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
          data-testid="admin-redirect-btn"
        >
          Zum Admin-Bereich
        </button>
      </div>
    );
  }

  // Mobile: Full-screen workspace experience
  if (isMobile) {
    return <WorkspaceMobile />;
  }

  // Desktop: Project list
  return <DesktopWorkspace user={user} navigate={navigate} />;
}
